import { supabase } from '@/integrations/supabase/client';
import { CRUDService } from '@/lib/services/CRUDService';
import { ServiceResponse } from '@/lib/services/BaseService';

export interface NutritionGoal {
  id: string;
  user_id: string;
  daily_calories: number;
  protein_grams: number;
  carbs_grams: number;
  fats_grams: number;
  goal_type: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FoodItem {
  id: string;
  name: string;
  brand?: string;
  calories_per_100g: number;
  protein_per_100g: number;
  carbs_per_100g: number;
  fats_per_100g: number;
  fiber_per_100g?: number;
  category?: string;
  verified: boolean;
}

export interface Meal {
  id: string;
  user_id: string;
  meal_type: string;
  meal_date: string;
  total_calories: number;
  total_protein: number;
  total_carbs: number;
  total_fats: number;
  photo_url?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface FoodEntry {
  id: string;
  meal_id: string;
  food_id?: string;
  custom_food_name?: string;
  quantity_grams: number;
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  fiber?: number;
  ai_estimated: boolean;
  confidence_score?: number;
  created_at: string;
}

export interface NutritionDailySummary {
  id: string;
  user_id: string;
  date: string;
  total_calories: number;
  total_protein: number;
  total_carbs: number;
  total_fats: number;
  total_fiber: number;
  calories_burned: number;
  net_calories: number;
  goal_calories?: number;
  goal_protein?: number;
  goal_carbs?: number;
  goal_fats?: number;
  created_at: string;
  updated_at: string;
}

export interface FoodPhotoAnalysis {
  id: string;
  user_id: string;
  photo_url: string;
  identified_foods: any;
  estimated_portions: any;
  analysis_result: any;
  confidence_score: number;
  processing_status: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export class NutritionService extends CRUDService {
  private static readonly COMPONENT_NAME = 'NutritionService';

  // Nutrition Goals
  static async getCurrentGoals(userId: string): Promise<ServiceResponse<NutritionGoal | null>> {
    return this.handleRequest(async () => {
      const query = supabase
        .from('nutrition_goals')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      return await query;
    }, { componentName: this.COMPONENT_NAME });
  }

  static async setGoals(userId: string, goals: {
    daily_calories: number;
    protein_grams: number;
    carbs_grams: number;
    fats_grams: number;
    goal_type?: string;
  }): Promise<ServiceResponse<NutritionGoal>> {
    return this.handleRequest(async () => {
      // Deactivate current goals first
      await supabase
        .from('nutrition_goals')
        .update({ is_active: false })
        .eq('user_id', userId)
        .eq('is_active', true);

      // Create new goal
      const query = supabase
        .from('nutrition_goals')
        .insert({
          user_id: userId,
          daily_calories: goals.daily_calories,
          protein_grams: goals.protein_grams,
          carbs_grams: goals.carbs_grams,
          fats_grams: goals.fats_grams,
          goal_type: goals.goal_type || 'maintain',
          is_active: true
        })
        .select()
        .single();

      return await query;
    }, { componentName: this.COMPONENT_NAME });
  }

  // Food Database
  static async searchFoods(query: string, limit = 20): Promise<ServiceResponse<FoodItem[]>> {
    return this.handleRequest(async () => {
      const searchQuery = supabase
        .from('food_database')
        .select('*')
        .or(`name.ilike.%${query}%, brand.ilike.%${query}%`)
        .eq('verified', true)
        .order('name')
        .limit(limit);

      return await searchQuery;
    }, { componentName: this.COMPONENT_NAME });
  }

  static async getFoodById(foodId: string): Promise<ServiceResponse<FoodItem | null>> {
    return this.handleRequest(async () => {
      const query = supabase
        .from('food_database')
        .select('*')
        .eq('id', foodId)
        .maybeSingle();

      return await query;
    }, { componentName: this.COMPONENT_NAME });
  }

  // Meals
  static async createMeal(userId: string, mealData: Partial<Meal>): Promise<ServiceResponse<Meal>> {
    return this.createEntry('meals', {
      user_id: userId,
      ...mealData
    }, { componentName: this.COMPONENT_NAME });
  }

  static async getMeals(userId: string, startDate?: string, endDate?: string): Promise<ServiceResponse<Meal[]>> {
    return this.handleRequest(async () => {
      let query = supabase
        .from('meals')
        .select('*')
        .eq('user_id', userId)
        .order('meal_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (startDate) {
        query = query.gte('meal_date', startDate);
      }
      if (endDate) {
        query = query.lte('meal_date', endDate);
      }

      return await query;
    }, { componentName: this.COMPONENT_NAME });
  }

  static async updateMeal(mealId: string, mealData: Partial<Meal>): Promise<ServiceResponse<Meal>> {
    return this.handleRequest(async () => {
      const { data, error } = await supabase
        .from('meals')
        .update(mealData)
        .eq('id', mealId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    }, { componentName: this.COMPONENT_NAME });
  }

  static async deleteMeal(mealId: string): Promise<ServiceResponse<void>> {
    return this.handleRequest(async () => {
      // First delete all food entries for this meal
      const { error: foodEntriesError } = await supabase
        .from('food_entries')
        .delete()
        .eq('meal_id', mealId);

      if (foodEntriesError) throw foodEntriesError;

      // Then delete the meal itself
      const { error: mealError } = await supabase
        .from('meals')
        .delete()
        .eq('id', mealId);

      if (mealError) throw mealError;
      return { data: undefined, error: null };
    }, { componentName: this.COMPONENT_NAME });
  }

  static async copyMeal(mealId: string, targetDate: string): Promise<ServiceResponse<Meal>> {
    return this.handleRequest(async () => {
      // Get the original meal with its food entries
      const { data: originalMeal, error: mealError } = await supabase
        .from('meals')
        .select(`
          *,
          food_entries (*)
        `)
        .eq('id', mealId)
        .single();

      if (mealError) throw mealError;

      // Create new meal
      const { data: newMeal, error: newMealError } = await supabase
        .from('meals')
        .insert({
          user_id: originalMeal.user_id,
          meal_type: originalMeal.meal_type,
          meal_date: targetDate,
          total_calories: originalMeal.total_calories,
          total_protein: originalMeal.total_protein,
          total_carbs: originalMeal.total_carbs,
          total_fats: originalMeal.total_fats,
          notes: originalMeal.notes
        })
        .select()
        .single();

      if (newMealError) throw newMealError;

      // Copy food entries if they exist
      if (originalMeal.food_entries && originalMeal.food_entries.length > 0) {
        const foodEntriesToInsert = originalMeal.food_entries.map((entry: any) => ({
          meal_id: newMeal.id,
          food_id: entry.food_id,
          custom_food_name: entry.custom_food_name,
          quantity_grams: entry.quantity_grams,
          calories: entry.calories,
          protein: entry.protein,
          carbs: entry.carbs,
          fats: entry.fats,
          fiber: entry.fiber,
          ai_estimated: entry.ai_estimated,
          confidence_score: entry.confidence_score
        }));

        const { error: entriesError } = await supabase
          .from('food_entries')
          .insert(foodEntriesToInsert);

        if (entriesError) throw entriesError;
      }

      return { data: newMeal, error: null };
    }, { componentName: this.COMPONENT_NAME });
  }

  static async getRecentMeals(userId: string, limit = 10): Promise<ServiceResponse<Meal[]>> {
    return this.getRecent('meals', userId, limit, { componentName: this.COMPONENT_NAME });
  }

  // Food Entries
  static async addFoodToMeal(mealId: string, foodEntry: Partial<FoodEntry>): Promise<ServiceResponse<FoodEntry>> {
    return this.createEntry('food_entries', {
      meal_id: mealId,
      ...foodEntry
    }, { componentName: this.COMPONENT_NAME });
  }

  static async getFoodEntries(mealId: string): Promise<ServiceResponse<FoodEntry[]>> {
    return this.handleRequest(async () => {
      const query = supabase
        .from('food_entries')
        .select(`
          *,
          food_database (
            name,
            brand,
            category
          )
        `)
        .eq('meal_id', mealId)
        .order('created_at');

      return await query;
    }, { componentName: this.COMPONENT_NAME });
  }

  static async updateFoodEntry(entryId: string, updates: Partial<FoodEntry>): Promise<ServiceResponse<FoodEntry>> {
    return this.updateEntry('food_entries', entryId, updates, { componentName: this.COMPONENT_NAME });
  }

  static async deleteFoodEntry(entryId: string): Promise<ServiceResponse<null>> {
    return this.deleteEntry('food_entries', entryId, { componentName: this.COMPONENT_NAME });
  }

  // Daily Summaries
  static async getDailySummary(userId: string, date: string): Promise<ServiceResponse<NutritionDailySummary | null>> {
    return this.handleRequest(async () => {
      const query = supabase
        .from('nutrition_daily_summary')
        .select('*')
        .eq('user_id', userId)
        .eq('date', date)
        .maybeSingle();

      return await query;
    }, { componentName: this.COMPONENT_NAME });
  }

  static async getWeeklySummaries(userId: string, weeks = 4): Promise<ServiceResponse<NutritionDailySummary[]>> {
    return this.handleRequest(async () => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - (weeks * 7));

      const query = supabase
        .from('nutrition_daily_summary')
        .select('*')
        .eq('user_id', userId)
        .gte('date', startDate.toISOString().split('T')[0])
        .order('date', { ascending: false });

      return await query;
    }, { componentName: this.COMPONENT_NAME });
  }

  // AI Photo Analysis
  static async analyzePhoto(userId: string, photoUrl: string): Promise<ServiceResponse<FoodPhotoAnalysis>> {
    return this.handleRequest(async () => {
      // Create initial record
      const { data: analysisRecord, error: createError } = await supabase
        .from('food_photo_analysis')
        .insert({
          user_id: userId,
          photo_url: photoUrl,
          processing_status: 'pending'
        })
        .select()
        .single();

      if (createError) throw createError;

      // Call AI analysis edge function
      const { data: analysisResult, error: analysisError } = await supabase.functions.invoke(
        'ai-nutrition-analysis',
        {
          body: {
            analysisId: analysisRecord.id,
            photoUrl: photoUrl,
            userId: userId
          }
        }
      );

      if (analysisError) {
        // Update record with error
        await supabase
          .from('food_photo_analysis')
          .update({
            processing_status: 'failed',
            error_message: analysisError.message
          })
          .eq('id', analysisRecord.id);
        
        throw analysisError;
      }

      return { data: analysisResult?.data || analysisRecord, error: null };
    }, { componentName: this.COMPONENT_NAME });
  }

  static async getPhotoAnalysis(analysisId: string): Promise<ServiceResponse<FoodPhotoAnalysis | null>> {
    return this.handleRequest(async () => {
      const query = supabase
        .from('food_photo_analysis')
        .select('*')
        .eq('id', analysisId)
        .maybeSingle();

      return await query;
    }, { componentName: this.COMPONENT_NAME });
  }

  // Nutrition Insights
  static async getNutritionInsights(userId: string, days = 30): Promise<ServiceResponse<any>> {
    return this.handleSupabaseFunction(
      'nutrition-insights',
      {
        userId,
        days
      },
      { componentName: this.COMPONENT_NAME }
    );
  }

  // Calculate net calories (consumed - burned)
  static async calculateNetCalories(userId: string, date: string): Promise<ServiceResponse<{
    consumed: number;
    burned: number;
    net: number;
  }>> {
    return this.handleRequest(async () => {
      // Get daily nutrition summary
      const { data: nutritionData } = await supabase
        .from('nutrition_daily_summary')
        .select('total_calories, calories_burned')
        .eq('user_id', userId)
        .eq('date', date)
        .maybeSingle();

      const consumed = nutritionData?.total_calories || 0;
      const burned = nutritionData?.calories_burned || 0;

      return {
        data: {
          consumed,
          burned,
          net: consumed - burned
        },
        error: null
      };
    }, { componentName: this.COMPONENT_NAME });
  }
}